import React, { useState, useEffect, useMemo } from 'react';
import {
  Activity,
  BarChart3,
  TrendingUp,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  Users,
  Calendar,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { callLogsAPI, extensionsAPI, sipTrunksAPI, callQueuesAPI } from '../services/api';

const Reports = () => {
  const [dateRange, setDateRange] = useState('today');
  const [callStats, setCallStats] = useState({
    totalCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    inboundCalls: 0,
    outboundCalls: 0,
    internalCalls: 0,
    answerRate: 0
  });
  const [extensions, setExtensions] = useState([]);
  const [sipTrunks, setSipTrunks] = useState([]);
  const [callQueues, setCallQueues] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, extsRes, trunksRes, queuesRes] = await Promise.all([
        callLogsAPI.getStats(),
        extensionsAPI.list(),
        sipTrunksAPI.list(),
        callQueuesAPI.list()
      ]);

      setCallStats({
        totalCalls: statsRes.data.total_calls || 0,
        answeredCalls: statsRes.data.answered || 0,
        missedCalls: statsRes.data.missed || 0,
        inboundCalls: statsRes.data.inbound || 0,
        outboundCalls: statsRes.data.outbound || 0,
        internalCalls: statsRes.data.internal || 0,
        answerRate: statsRes.data.answer_rate || 0
      });
      setExtensions(extsRes.data);
      setSipTrunks(trunksRes.data);
      setCallQueues(queuesRes.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    }
  };

  // Memoized extension activity with simulated data
  const extensionActivity = useMemo(() => {
    return extensions.slice(0, 5).map((ext, index) => ({
      ...ext,
      calls: 10 + (index * 7) % 40,
      duration: `${15 + index * 3}:${((index * 17) % 60).toString().padStart(2, '0')}`
    }));
  }, [extensions]);

  // Memoized trunk usage
  const trunkUsage = useMemo(() => {
    return sipTrunks.map((trunk, index) => ({
      ...trunk,
      callCount: 50 + (index * 47) % 150,
      percentage: 30 + (index * 23) % 70
    }));
  }, [sipTrunks]);

  // Memoized hourly data
  const hourlyData = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, '0')}:00`,
      calls: (i >= 9 && i <= 17) ? 25 + (i % 5) * 5 : 5 + (i % 3) * 2
    }));
  }, []);

  // Destructure for easy access
  const { totalCalls, answeredCalls, missedCalls, inboundCalls, outboundCalls, internalCalls, answerRate } = callStats;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-gray-400 mt-1">Analytics and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Calls</p>
                <p className="text-3xl font-bold text-white mt-2">{callStats.totalCalls}</p>
                <p className="text-sm text-green-400 mt-1">+12% vs last period</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Phone className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Answer Rate</p>
                <p className="text-3xl font-bold text-white mt-2">{answerRate}%</p>
                <p className="text-sm text-green-400 mt-1">+5% vs last period</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Avg Call Duration</p>
                <p className="text-3xl font-bold text-white mt-2">4:32</p>
                <p className="text-sm text-yellow-400 mt-1">-2% vs last period</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <Clock className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Active Extensions</p>
                <p className="text-3xl font-bold text-white mt-2">{extensions.filter(e => e.status === 'online').length}</p>
                <p className="text-sm text-gray-400 mt-1">of {extensions.length} total</p>
              </div>
              <div className="p-3 bg-cyan-500/20 rounded-xl">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#1e1e2d] border border-[#2a2a3d]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="extensions" className="data-[state=active]:bg-blue-600">
            <Users className="w-4 h-4 mr-2" />
            Extensions
          </TabsTrigger>
          <TabsTrigger value="trunks" className="data-[state=active]:bg-blue-600">
            <Phone className="w-4 h-4 mr-2" />
            Trunks
          </TabsTrigger>
          <TabsTrigger value="queues" className="data-[state=active]:bg-blue-600">
            <Activity className="w-4 h-4 mr-2" />
            Queues
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Call Distribution */}
            <Card className="bg-[#12121a] border-[#1e1e2d]">
              <CardHeader>
                <CardTitle className="text-white">Call Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PhoneIncoming className="w-5 h-5 text-green-400" />
                      <span className="text-white">Inbound</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-bold">{inboundCalls}</span>
                      <Badge className="bg-green-500/20 text-green-400">
                        {Math.round((inboundCalls / totalCalls) * 100)}%
                      </Badge>
                    </div>
                  </div>
                  <Progress value={(inboundCalls / totalCalls) * 100} className="h-2 bg-[#1e1e2d]" />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PhoneOutgoing className="w-5 h-5 text-blue-400" />
                      <span className="text-white">Outbound</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-bold">{outboundCalls}</span>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        {Math.round((outboundCalls / totalCalls) * 100)}%
                      </Badge>
                    </div>
                  </div>
                  <Progress value={(outboundCalls / totalCalls) * 100} className="h-2 bg-[#1e1e2d]" />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-purple-400" />
                      <span className="text-white">Internal</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-bold">{internalCalls}</span>
                      <Badge className="bg-purple-500/20 text-purple-400">
                        {Math.round((internalCalls / totalCalls) * 100)}%
                      </Badge>
                    </div>
                  </div>
                  <Progress value={(internalCalls / totalCalls) * 100} className="h-2 bg-[#1e1e2d]" />
                </div>
              </CardContent>
            </Card>

            {/* Call Status */}
            <Card className="bg-[#12121a] border-[#1e1e2d]">
              <CardHeader>
                <CardTitle className="text-white">Call Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div>
                      <p className="text-green-400 font-medium">Answered</p>
                      <p className="text-2xl font-bold text-white">{answeredCalls}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-400">{answerRate}%</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div>
                      <p className="text-red-400 font-medium">Missed/No Answer</p>
                      <p className="text-2xl font-bold text-white">{missedCalls}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-red-400">{100 - answerRate}%</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hourly Distribution */}
            <Card className="bg-[#12121a] border-[#1e1e2d] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white">Hourly Call Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40">
                  {hourlyData.map((data, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-blue-500/40 hover:bg-blue-500/60 transition-colors rounded-t"
                        style={{ height: `${(data.calls / 50) * 100}%` }}
                        title={`${data.hour}: ${data.calls} calls`}
                      />
                      {index % 4 === 0 && (
                        <span className="text-xs text-gray-500 mt-2">{data.hour}</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Extensions Tab */}
        <TabsContent value="extensions" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Extension Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1e1e2d]">
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Extension</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Name</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Department</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Calls</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Total Duration</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {extensionActivity.map((ext) => (
                    <tr key={ext.id} className="border-b border-[#1e1e2d] hover:bg-[#1e1e2d]/50">
                      <td className="p-4 font-mono text-white">{ext.id}</td>
                      <td className="p-4 text-white">{ext.name}</td>
                      <td className="p-4 text-gray-400">{ext.department}</td>
                      <td className="p-4 text-white font-bold">{ext.calls}</td>
                      <td className="p-4 text-white font-mono">{ext.duration}</td>
                      <td className="p-4">
                        <Badge className={ext.status === 'online' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                        }>
                          {ext.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trunks Tab */}
        <TabsContent value="trunks" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Trunk Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {trunkUsage.map((trunk) => (
                  <div key={trunk.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${trunk.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-white font-medium">{trunk.name}</span>
                        <Badge variant="outline" className="border-[#2a2a3d] text-gray-400">
                          {trunk.provider}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-white font-bold">{trunk.callCount} calls</span>
                        <span className="text-gray-400">{trunk.usedChannels}/{trunk.channels} channels</span>
                      </div>
                    </div>
                    <Progress value={trunk.percentage} className="h-2 bg-[#1e1e2d]" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Queues Tab */}
        <TabsContent value="queues" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {callQueues.map((queue) => (
              <Card key={queue.id} className="bg-[#12121a] border-[#1e1e2d]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">{queue.name}</CardTitle>
                    <Badge className={queue.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-gray-500/20 text-gray-400'
                    }>
                      {queue.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-[#1e1e2d] rounded-lg">
                      <p className="text-2xl font-bold text-green-400">{queue.currentCalls}</p>
                      <p className="text-xs text-gray-400">Active Calls</p>
                    </div>
                    <div className="text-center p-3 bg-[#1e1e2d] rounded-lg">
                      <p className="text-2xl font-bold text-yellow-400">{queue.waitingCalls}</p>
                      <p className="text-xs text-gray-400">Waiting</p>
                    </div>
                    <div className="text-center p-3 bg-[#1e1e2d] rounded-lg">
                      <p className="text-2xl font-bold text-white">{queue.avgWaitTime}</p>
                      <p className="text-xs text-gray-400">Avg Wait</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#1e1e2d]">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Agents Online</span>
                      <span className="text-white font-bold">{queue.agents.length}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
